import React, { useState } from 'react';
import { CheckCircle2, Loader2, Calendar, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { careersAPI } from '../services/api';

const CALCOM_BOOKING_URL = 'https://app.cal.eu/humanistix/30min';

const copy = {
  nl: {
    heroBadge: 'Jobs @ Humanistix',
    heroTitle: 'Werk aan de menselijke kant van AI',
    heroText: 'Wij helpen HR-teams AI adopteren zonder hype, met meetbare impact. Klein team, grote ambitie — en plaats voor mensen die net als wij geloven dat technologie pas werkt als mensen mee zijn.',
    futureRole: 'Future role',
    role1Title: 'AI for HR Change & Transformation Consultants',
    role1Text: 'Voor mensen die verandering niet als buzzword gebruiken maar als vak beheersen. Je helpt organisaties AI adopteren op een manier die realistisch, gedragen en meetbaar is.',
    role1Bullets: ['AI-adoptie vertalen naar echte HR-praktijk', 'Leidinggevenden en teams meenemen in verandering', 'Van tool-focus naar werkelijke impact'],
    role2Title: 'AI for HR Vibe Coders',
    role2Text: "Voor makers die technologie snel tastbaar maken, maar nooit vergeten dat mensen geen API's zijn. Slim bouwen, helder denken, empathisch ontwerpen.",
    role2Bullets: ['Prototypes en slimme automations bouwen', 'Experimenteren met agents en workflows', 'UX maken die menselijk aanvoelt'],
    spontaneousTitle: 'Spontaan solliciteren, maar dan zonder stijf gedoe',
    spontaneousText: 'Hier mag je gerust spontaan solliciteren. Graag zelfs. Niet met een brave standaardbrief die elk bedrijf kan krijgen, maar met een eerlijk verhaal over wat jij zou willen bouwen of verbeteren.',
    bonusText: 'Bonus: wie inhoudelijk interessant reageert, krijgt van ons ook iets terug: een kort verkennend gesprek of een relevante AI-for-HR insight.',
    formTitle: 'Laat iets van jezelf zien',
    namePlaceholder: 'Naam',
    emailPlaceholder: 'E-mailadres',
    linkedinPlaceholder: 'LinkedIn of portfolio',
    motivationPlaceholder: 'Waar zou jij bij Humanistix het verschil maken?',
    submitBtn: 'Verstuur spontane sollicitatie',
    submitting: 'Versturen...',
    successTitle: 'Sollicitatie verstuurd!',
    successText: 'Bedankt voor je eerlijke verhaal. Als het inhoudelijk interessant is, hoor je snel van ons.',
    errorText: 'Er ging iets mis bij het versturen. Probeer het opnieuw.',
    validationText: 'Vul minstens je naam, e-mailadres en verhaal in.',
    ctaTitle: 'Misschien hebben we vandaag geen vacature. Misschien zoeken we jou toch al.',
    ctaText: 'Als je houdt van AI, HR, verandering en een tikje gezonde scherpte, dan is dit waarschijnlijk een gesprek waard.',
    ctaBtn: 'Ik wil kennismaken',
  },
  en: {
    heroBadge: 'Jobs @ Humanistix',
    heroTitle: 'Work on the human side of AI',
    heroText: "We help HR teams adopt AI without the hype, with measurable impact. Small team, big ambition — and room for people who, like us, believe technology only works when people are on board.",
    futureRole: 'Future role',
    role1Title: 'AI for HR Change & Transformation Consultants',
    role1Text: 'For people who don\'t use change as a buzzword but master it as a craft. You help organisations adopt AI in a way that is realistic, supported and measurable.',
    role1Bullets: ['Translating AI adoption into real HR practice', 'Guiding leaders and teams through change', 'From tool focus to real impact'],
    role2Title: 'AI for HR Vibe Coders',
    role2Text: "For makers who make technology tangible fast, but never forget that people aren't APIs. Build smart, think clearly, design with empathy.",
    role2Bullets: ['Building prototypes and smart automations', 'Experimenting with agents and workflows', 'Creating UX that feels human'],
    spontaneousTitle: 'Spontaneous applications, without the stiff formalities',
    spontaneousText: 'Feel free to apply spontaneously. Please do. Not with a polite standard letter any company could receive, but with an honest story about what you would want to build or improve.',
    bonusText: "Bonus: if your response is genuinely interesting, you'll get something back from us too: a short exploratory chat or a relevant AI-for-HR insight.",
    formTitle: 'Show us something of yourself',
    namePlaceholder: 'Name',
    emailPlaceholder: 'Email address',
    linkedinPlaceholder: 'LinkedIn or portfolio',
    motivationPlaceholder: 'Where would you make the difference at Humanistix?',
    submitBtn: 'Send spontaneous application',
    submitting: 'Sending...',
    successTitle: 'Application sent!',
    successText: "Thanks for your honest story. If it's genuinely interesting, you'll hear from us soon.",
    errorText: 'Something went wrong while sending. Please try again.',
    validationText: 'Please fill in at least your name, email address and story.',
    ctaTitle: "Maybe we don't have a vacancy today. Maybe we're already looking for you.",
    ctaText: 'If you love AI, HR, change and a healthy dose of sharpness, this is probably a conversation worth having.',
    ctaBtn: "Let's talk",
  },
};

const RoleCard = ({ title, text, bullets, badge }) => (
  <div className="bg-white rounded-2xl shadow-sm p-8">
    <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-4" style={{ backgroundColor: '#fde8e1', color: '#e2725b' }}>
      {badge}
    </span>
    <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
    <p className="text-gray-600 mb-4 leading-relaxed">{text}</p>
    <ul className="list-disc list-inside space-y-1.5 text-gray-700">
      {bullets.map((b) => (
        <li key={b}>{b}</li>
      ))}
    </ul>
  </div>
);

export default function CareersPage() {
  const { language } = useLanguage();
  const t = copy[language] || copy.nl;

  const [form, setForm] = useState({ name: '', email: '', linkedin: '', motivation: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.motivation.trim()) {
      setError(t.validationText);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await careersAPI.submitApplication({
        name: form.name,
        email: form.email,
        linkedin: form.linkedin || null,
        motivation: form.motivation,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Application submit failed:', err);
      setError(t.errorText);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors bg-white';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" data-testid="careers-page">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
          <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-5 bg-blue-100 text-blue-600">
            {t.heroBadge}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-5 max-w-3xl leading-tight">
            {t.heroTitle}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">{t.heroText}</p>
        </section>

        {/* Future roles */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <RoleCard badge={t.futureRole} title={t.role1Title} text={t.role1Text} bullets={t.role1Bullets} />
            <RoleCard badge={t.futureRole} title={t.role2Title} text={t.role2Text} bullets={t.role2Bullets} />
          </div>
        </section>

        {/* Spontaneous application + form */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-snug">{t.spontaneousTitle}</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">{t.spontaneousText}</p>
              <div className="rounded-xl p-5 bg-blue-50">
                <p className="font-semibold leading-relaxed text-blue-800">{t.bonusText}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{t.formTitle}</h2>
              {submitted ? (
                <div className="text-center py-8" data-testid="careers-success-message">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-blue-100">
                    <CheckCircle2 className="h-8 w-8 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t.successTitle}</h3>
                  <p className="text-gray-600">{t.successText}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="careers-apply-form">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t.namePlaceholder}
                    className={inputClass}
                    data-testid="careers-name-input"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={t.emailPlaceholder}
                    className={inputClass}
                    data-testid="careers-email-input"
                  />
                  <input
                    type="text"
                    value={form.linkedin}
                    onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                    placeholder={t.linkedinPlaceholder}
                    className={inputClass}
                    data-testid="careers-linkedin-input"
                  />
                  <textarea
                    value={form.motivation}
                    onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                    placeholder={t.motivationPlaceholder}
                    rows={5}
                    className={`${inputClass} font-mono text-sm resize-y`}
                    data-testid="careers-motivation-input"
                  />
                  {error && (
                    <p className="text-sm text-red-600" data-testid="careers-form-error">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600"
                    data-testid="careers-submit-btn"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submitting ? t.submitting : t.submitBtn}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="rounded-2xl p-10 md:p-14 text-center bg-gradient-to-br from-white to-blue-50">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 max-w-3xl mx-auto leading-snug">
              {t.ctaTitle}
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">{t.ctaText}</p>
            <a
              href={CALCOM_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-semibold px-8 py-3.5 rounded-xl transition-colors bg-blue-500 hover:bg-blue-600"
              data-testid="careers-meet-btn"
            >
              <Calendar className="h-5 w-5" />
              {t.ctaBtn}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
